/**
 * Auto-translate bootstrap for the React i18next runtime.
 *
 * How it works:
 *   1. On every language change (and on first mount), we walk the
 *      in-memory HR resource bundle — this is our "source of truth".
 *   2. For each key whose target-locale resource is missing or still
 *      identical to the HR version (a strong hint it was never properly
 *      translated), we queue the HR text for translation.
 *   3. We batch-POST the queued pairs to
 *      `POST /api/public/translate/ui` which caches every result in the
 *      server-side `translation_cache` collection and returns the full
 *      map. The server picks the active provider (Gemini / LibreTranslate
 *      / Google) as configured by an admin.
 *   4. Translated strings are merged into the i18next resource bundle
 *      via `i18n.addResourceBundle(lang, 'translation', ..., true, true)`
 *      so every React component re-renders automatically thanks to the
 *      `useTranslation()` hook subscription.
 *   5. We also persist the merged bundle to localStorage
 *      (`emina_i18n_cache_{lang}`) so the very first render after a
 *      language switch on a subsequent visit is already translated.
 */
import i18n from './index';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_VERSION = 'v1';
const STORAGE_PREFIX = `emina_i18n_${CACHE_VERSION}_`;
const MAX_ITEMS_PER_CALL = 180;
const DYNAMIC_STORE_KEY = `${STORAGE_PREFIX}dynamic`;

// Keys whose English fallback value should NOT be used to detect "missing"
// translations — they're intentionally kept in the source tongue (HTML
// entities, currency placeholders, etc).
const IGNORE_IDENTITY_KEYS = new Set([
  'footer.rights', 'breaking.label', 'sponsored.discount_badge',
  'advertising_extra.long_term_badge',
]);

// Languages whose native script is NOT Latin — we use this to detect
// locale bundles that were filled with English placeholders instead of
// real translations, and force-refresh them via the live translator.
const NON_LATIN_LANGS = new Set(['zh', 'ja', 'ar', 'hi', 'ru']);
const NON_LATIN_SCRIPT = /[\u4E00-\u9FFF\u3040-\u30FF\u0600-\u06FF\u0900-\u097F\u0400-\u04FF]/;

function flatten(obj, prefix = '', out = {}) {
  for (const [k, v] of Object.entries(obj || {})) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      flatten(v, key, out);
    } else if (typeof v === 'string') {
      out[key] = v;
    }
  }
  return out;
}

function unflatten(flat) {
  const out = {};
  for (const [key, v] of Object.entries(flat || {})) {
    const parts = key.split('.');
    let cur = out;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (!cur[p] || typeof cur[p] !== 'object') cur[p] = {};
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = v;
  }
  return out;
}

function loadLocalCache(lang) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + lang);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveLocalCache(lang, flat) {
  try { localStorage.setItem(STORAGE_PREFIX + lang, JSON.stringify(flat)); } catch {}
}

function loadDynamicCache() {
  try {
    const raw = localStorage.getItem(DYNAMIC_STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveDynamicCache(obj) {
  try { localStorage.setItem(DYNAMIC_STORE_KEY, JSON.stringify(obj)); } catch {}
}

// ─────────────────────────── UI bundle sync ───────────────────────────

async function translateBatch(items, target) {
  if (!items.length) return {};
  try {
    const r = await fetch(`${API}/public/translate/ui`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target, source: 'hr', items }),
    });
    if (!r.ok) return {};
    const d = await r.json();
    return d.translations || {};
  } catch { return {}; }
}

let _inflight = null;

async function ensureI18nFor(lang) {
  if (!lang || lang === 'hr') return;
  // Merge any previously persisted localStorage cache into the bundle so the
  // UI flips to the target language immediately.
  const localCache = loadLocalCache(lang);
  if (Object.keys(localCache).length) {
    i18n.addResourceBundle(lang, 'translation', unflatten(localCache), true, true);
  }

  // Walk the HR resource bundle and identify missing keys.
  const hrRes = i18n.getResourceBundle('hr', 'translation') || {};
  const hrFlat = flatten(hrRes);
  const tgtRes = i18n.getResourceBundle(lang, 'translation') || {};
  const tgtFlat = flatten(tgtRes);

  const missing = [];
  const nonLatinTarget = NON_LATIN_LANGS.has(lang);
  for (const [key, hrVal] of Object.entries(hrFlat)) {
    if (!hrVal) continue;
    if (IGNORE_IDENTITY_KEYS.has(key)) continue;
    const tgtVal = tgtFlat[key];
    if (!tgtVal) {
      missing.push({ key, text: hrVal });
      continue;
    }
    // Identical to HR → retranslate (key was never localised).
    if (tgtVal === hrVal) {
      missing.push({ key, text: hrVal });
      continue;
    }
    // Non-Latin target locale but stored value is Latin-only → previous
    // bundle was seeded with English fallback. Schedule a proper retranslate.
    if (nonLatinTarget && !NON_LATIN_SCRIPT.test(tgtVal)) {
      missing.push({ key, text: hrVal });
    }
  }
  if (!missing.length) return;

  // Batch into chunks.
  const chunks = [];
  for (let i = 0; i < missing.length; i += MAX_ITEMS_PER_CALL) {
    chunks.push(missing.slice(i, i + MAX_ITEMS_PER_CALL));
  }

  const merged = { ...tgtFlat };
  for (const chunk of chunks) {
    const out = await translateBatch(chunk, lang);
    for (const it of chunk) {
      const v = out[it.key];
      if (v) merged[it.key] = v;
    }
  }
  // Persist + merge into i18next so every <Trans>/t() call re-resolves.
  saveLocalCache(lang, merged);
  i18n.addResourceBundle(lang, 'translation', unflatten(merged), true, true);
  // addResourceBundle does NOT automatically re-render React components
  // that use `useTranslation()`; only 'languageChanged' / 'loaded' do.
  // Fire a synthetic event so subscribers refresh with the merged bundle.
  try { i18n.emit('languageChanged', lang); } catch {}
}

// ─────────────────────────── Dynamic string (content) translation ───────────────────────────

// Micro-batching queue: when several components call translateDynamic in the
// same tick (e.g. 20 ArticleCards rendering after a language change), we
// collect all pending texts for ~25ms and fire a single /translate/ui call
// instead of 20 parallel ones. Every waiter gets its answer from the same
// response.
const _pendingByLang = new Map(); // lang → { map: text→[resolve,reject], timer }

function _scheduleDynamicTranslate(text, targetLang) {
  let entry = _pendingByLang.get(targetLang);
  if (!entry) {
    entry = { map: new Map(), timer: null };
    _pendingByLang.set(targetLang, entry);
  }
  return new Promise((resolve, reject) => {
    const bucket = entry.map.get(text) || [];
    bucket.push({ resolve, reject });
    entry.map.set(text, bucket);
    if (entry.timer) return;
    entry.timer = setTimeout(async () => {
      _pendingByLang.delete(targetLang);
      const store = loadDynamicCache();
      const langBucket = store[targetLang] || {};
      const texts = Array.from(entry.map.keys());
      const items = texts.map((t, idx) => ({ key: String(idx), text: t }));
      let result = {};
      try {
        // chunk if huge
        for (let i = 0; i < items.length; i += MAX_ITEMS_PER_CALL) {
          const chunk = items.slice(i, i + MAX_ITEMS_PER_CALL);
          const res = await translateBatch(chunk, targetLang);
          Object.assign(result, res);
        }
      } catch {}
      texts.forEach((t, idx) => {
        const v = result[String(idx)] || t;
        if (v && v !== t) langBucket[t] = v;
        const waiters = entry.map.get(t) || [];
        waiters.forEach(w => w.resolve(v));
      });
      store[targetLang] = langBucket;
      saveDynamicCache(store);
    }, 25);
  });
}

/**
 * translateDynamic — translate a single arbitrary string (e.g. an
 * admin-authored headline). Results are cached in localStorage so the
 * same string is never translated twice. Returns the translated value
 * or the original if translation fails/not needed.
 */
export async function translateDynamic(text, targetLang) {
  if (!text || !targetLang || targetLang === 'hr') return text;
  const store = loadDynamicCache();
  const bucket = store[targetLang] || {};
  if (bucket[text]) return bucket[text];
  try {
    return await _scheduleDynamicTranslate(text, targetLang);
  } catch {
    return text;
  }
}

/**
 * translateDynamicMany — translate a bag of strings at once. Returns
 * Record<original, translated>. Uses and updates the localStorage
 * dynamic cache.
 */
export async function translateDynamicMany(texts, targetLang) {
  if (!Array.isArray(texts) || !texts.length) return {};
  if (!targetLang || targetLang === 'hr') {
    return Object.fromEntries(texts.map(t => [t, t]));
  }
  const store = loadDynamicCache();
  const bucket = store[targetLang] || {};
  const out = {};
  const miss = [];
  for (const t of texts) {
    if (!t) { out[t] = t; continue; }
    if (bucket[t]) { out[t] = bucket[t]; continue; }
    miss.push(t);
  }
  for (let i = 0; i < miss.length; i += MAX_ITEMS_PER_CALL) {
    const chunk = miss.slice(i, i + MAX_ITEMS_PER_CALL);
    const items = chunk.map((text, idx) => ({ key: String(idx), text }));
    const res = await translateBatch(items, targetLang);
    chunk.forEach((text, idx) => {
      const v = res[String(idx)];
      if (v) {
        bucket[text] = v;
        out[text] = v;
      } else {
        out[text] = text;
      }
    });
  }
  store[targetLang] = bucket;
  saveDynamicCache(store);
  return out;
}

// ─────────────────────────── bootstrap ───────────────────────────

export function bootAutoTranslate() {
  // Kick off on first mount (user might already be on a non-HR language
  // because of their stored preference).
  const run = () => {
    if (_inflight) return _inflight;
    _inflight = ensureI18nFor(i18n.language).catch(() => {}).finally(() => { _inflight = null; });
    return _inflight;
  };
  run();
  i18n.on('languageChanged', () => { run(); });
}
